<ng-container *ngIf="loading; else realContent">
  <div class="skeleton-page">

    <div class="skeleton header"></div>

    <div class="skeleton overall-card"></div>

    <div class="skeleton section"></div>
    <div class="skeleton section"></div>

    <div class="skeleton section tall"></div>

    <div class="skeleton summary"></div>
  </div>
</ng-container>

<ng-template #realContent>

  <div class="container-fluid p-3">
    <div class="performance-report">
      <!-- Header -->
      <div class="report-header">
        <i class="bi bi-arrow-left-short back-icon" (click)="goback()"></i>
        <div class="title">
          <i class="bi bi-trophy-fill"></i>
          <h2>Interview Performance Report</h2>
        </div>
      </div>


      <p class="report-subtext">
        Here's your detailed performance analysis with actionable feedback.
      </p>

      <!-- Overall Performance -->
      <div class="overall-card" *ngIf="evaluationData; else noData">
        <div class="card-content">
          <h3>Overall Performance</h3>

          <p class="role">
            {{ role || '—' }}
            <span *ngIf="round"> - {{ round }}</span>
          </p>

          <p class="interviewer">
            <ng-container *ngIf="company">
              Company: <strong>{{ company }}</strong> •
            </ng-container>
            Duration: {{ duration }} mins
          </p>

          <p class="status" *ngIf="displayScore; else incompleteStatus">
            <i class="bi bi-star"></i>
            Interview completed successfully
          </p>

          <ng-template #incompleteStatus>
            <p class="status warning">
              <i class="bi bi-exclamation-triangle-fill"></i>
              Interview Incomplete
            </p>
          </ng-template>
        </div>

        <div class="score-section">
          <div class="circle" [ngStyle]="{ '--progress-angle': getProgressAngle(totalScore) }">
            <div class="inner-circle">
              <div class="score">{{ displayScore }}</div>
              <p class="outof">out of 100</p>
            </div>
          </div>
          <p class="rating-text">{{ feedbackLabel }}</p>
        </div>
      </div>

      <ng-container *ngIf="isPaid">
        <div class="zero-score-message" *ngIf="isInterviewIncomplete">
          <h3>Interview Incomplete</h3>
          <p>
            You exited the interview before answering any questions.
            Because no responses were recorded, your score is shown as
            <strong>0</strong> and no evaluation could be generated.
          </p>
        </div>
      </ng-container>

      <ng-template #noData>
        <div class="no-data">
          <i class="bi bi-info-circle"></i> No performance data available.
        </div>
      </ng-template>
    </div>


    <!-- ===================== PERFORMANCE BREAKDOWN ===================== -->
    <div class="performance-breakdown" *ngIf="performanceData?.length">
      <div class="header">
        <i class="bi bi-bar-chart-line-fill icon-header"></i>
        <h2>Performance Breakdown</h2>
      </div>

      <div class="metric" *ngFor="let item of performanceData">
        <div class="metric-header">
          <div class="metric-icon" [ngClass]="getScoreClass(item.score)">
            <i class="bi" [ngClass]="{
            'bi-chat-dots-fill': item.title === 'Communication',
            'bi-laptop-fill': item.title === 'Technical Knowledge',
            'bi-emoji-smile-fill': item.title === 'Confidence',
            'bi-graph-up-arrow': item.title === 'Structure'
          }"></i>
          </div>

          <div class="metric-info">
            <h3>{{ item.title }}</h3>
            <p>{{ item.subtitle }}</p>
          </div>
          <div class="metric-score" [ngClass]="getScoreClass(item.score)">
            <strong>{{ getOneDecimalScore(item.score) }}/10</strong>
            <span>{{ getScorePercent(item.score) }}%</span>
          </div>

        </div>

        <div class="progress-bar">
          <div class="progress-fill" [ngClass]="getScoreClass(item.score)" [style.width.%]="item.score * 10"></div>
        </div>
      </div>
    </div>

    <div class="locked-evaluation-row" *ngIf="!isPaid">
      <div class="lock-icon">
        <i class="bi bi-lock"></i>
      </div>
      <p class="locked-text">Full evaluation report locked</p>
      <button class="btn-add" routerLink="/student-dashboard/upgrade-plan">Upgrade now</button>
    </div>

    <!-- ===================== QUESTION NAVIGATOR ===================== -->
    <ng-container *ngIf="isPaid">
      <div class="question-navigator font-name" *ngIf="!isInterviewIncomplete">
        <div class="navigator-header">
          <i class="bi bi-send-fill"></i>
          <h2>Question Navigator</h2>
          <span class="question-count">({{ questions.length }} questions)</span>
        </div>

        <!-- Tabs -->
        <div class="question-tabs" *ngIf="questions?.length">
          <button *ngFor="let q of questions; let i = index" class="tab" [class.active]="selectedQuestion === i"
            (click)="selectQuestion(i)">
            Q{{ i + 1 }}
            <span>{{ q.score }}/10</span>
          </button>
        </div>

        <!-- Question Card -->
        <div class="question-card" *ngIf="questions?.length; else noQuestions">
          <h3>
            <span class="q-badge">Q{{ selectedQuestion + 1 }}</span>
            {{ questions[selectedQuestion].question }}
          </h3>

          <div class="score-box">
            <span class="score">
              Score: <strong>{{ questions[selectedQuestion].score }}/10</strong>
            </span>
            <span class="label" [ngClass]="getScoreClass(questions[selectedQuestion].score)">
              {{ getRatingText(questions[selectedQuestion].score) }}
            </span>
          </div>
        </div>

        <ng-template #noQuestions>
          <p class="no-data">No questions available for this interview.</p>
        </ng-template>

        <!-- Tabs for Subsection -->
        <div class="feedback-tabs" *ngIf="questions?.length">
          <div class="tab" [class.active]="activeSubTab === 'feedback'" (click)="activeSubTab = 'feedback'">
            <i class="bi bi-chat-left"></i> Detailed Feedback
          </div>
          <div class="tab" [class.active]="activeSubTab === 'improved'" (click)="activeSubTab = 'improved'">
            <i class="bi bi-lightbulb"></i> Feedback & Recommendations
          </div>
        </div>

        <!-- ========== DETAILED FEEDBACK SECTION ========== -->
        <div *ngIf="activeSubTab === 'feedback'" class="feedback-section">
          <div class="answer-box" *ngIf="questions[selectedQuestion].user_answer; else noAnswer">
            <h4>
              <i class="bi bi-chat-left-quote"></i>
              <span class="answer">Your Answer</span>
            </h4>
            <p>{{ questions[selectedQuestion].user_answer }}</p>
          </div>

          <ng-template #noAnswer>
            <p class="no-data">No answer provided for this question.</p>
          </ng-template>

          <div class="improved-section" *ngIf="questions[selectedQuestion].improved_answer; else noImproved">
            <h4><i class="bi bi-stars"></i> Improved Answer Example</h4>
            <div class="improved-answer">
              <p>{{ questions[selectedQuestion].improved_answer }}</p>
            </div>
          </div>

          <ng-template #noImproved>
            <div class="no-data">
              <i class="bi bi-info-circle"></i> No improved answer available.
            </div>
          </ng-template>
        </div>

        <!-- ========== IMPROVED ANSWER TAB ========== -->
        <div *ngIf="activeSubTab === 'improved'" class="improved-section">
          <h4><i class="bi bi-stars"></i> Deep Dive Feedback</h4>

          <div class="feedback-grid">
            <div class="feedback-item good">
              <h5><i class="bi bi-check-circle"></i> What Went Well</h5>
              <ul *ngIf="questions[selectedQuestion].what_went_well?.length; else noGood">
                <li *ngFor="let good of questions[selectedQuestion].what_went_well">
                  {{ good }}
                </li>
              </ul>
              <ng-template #noGood>
                <p class="no-data">No positive feedback available.</p>
              </ng-template>
            </div>

            <div class="feedback-item improve">
              <h5><i class="bi bi-bullseye"></i> Areas to Improve</h5>
              <ul *ngIf="questions[selectedQuestion].areas_to_improve?.length; else noImprove">
                <li *ngFor="let bad of questions[selectedQuestion].areas_to_improve">
                  {{ bad }}
                </li>
              </ul>
              <ng-template #noImprove>
                <p class="no-data">No improvement suggestions available.</p>
              </ng-template>
            </div>
          </div>
        </div>
      </div>
    </ng-container>

    <!-- ===================== PERFORMANCE SUMMARY ===================== -->
    <div class="performance-summary" *ngIf="isPaid">
      <div class="summary-card strengths">
        <div class="card-header">
          <i class="bi bi-graph-up-arrow"></i>
          <h3>Key Strengths</h3>
        </div>

        <ul class="summary-list">
          <ng-container *ngIf="!isInterviewIncomplete; else incompleteStrengths">
            <li *ngFor="let strength of strengths">
              <i class="bi bi-check2"></i>
              <span>{{ strength }}</span>
            </li>

            <li *ngIf="!strengths?.length" class="no-data">
              No strengths available.
            </li>
          </ng-container>
        </ul>

        <ng-template #incompleteStrengths>
          <div class="no-data">
            No strengths identified as the interview was not completed
          </div>
        </ng-template>
      </div>


      <div class="summary-card focus">
        <div class="card-header">
          <i class="bi bi-exclamation-triangle"></i>
          <h3>Focus Areas</h3>
        </div>

        <ul class="summary-list">
          <ng-container *ngIf="!isInterviewIncomplete; else incompleteFocus">
            <li *ngFor="let focus of focusAreas">
              <i class="bi bi-exclamation-lg"></i>
              <span>{{ focus }}</span>
            </li>

            <li *ngIf="!focusAreas?.length" class="no-data">
              No focus areas available.
            </li>
          </ng-container>
        </ul>

        <ng-template #incompleteFocus>
          <div class="no-data">
            Complete the full interview to receive a proper evaluation
          </div>
        </ng-template>
      </div>

      <div class="summary-card next-steps">
        <div class="card-header">
          <i class="bi bi-bullseye"></i>
          <h3>Recommended Next Steps</h3>
        </div>
        <ul class="summary-list steps">
          <li *ngFor="let step of nextSteps">
            <span class="dot" [ngClass]="step.color"></span> {{ step.text }}
          </li>
          <li *ngIf="!nextSteps?.length" class="no-data">No steps available.</li>
        </ul>
      </div>
    </div>
  </div>
</ng-template>
